// src/ResetPasswordConfirm.jsx
import React, { useMemo, useState } from "react";
import { auth } from "./firebase";
import { verifyPasswordResetCode, confirmPasswordReset } from "firebase/auth";

/**
 * Where to send the user after a successful reset.
 * Change to "/login" if you have a dedicated login route.
 */
const REDIRECT_PATH = "/";

export default function ResetPasswordConfirm() {
  // Parse query params once
  const { mode, oobCode, continueUrl } = useMemo(() => {
    try {
      const u = new URL(window.location.href);
      return {
        mode: u.searchParams.get("mode"),
        oobCode: u.searchParams.get("oobCode"),
        continueUrl: u.searchParams.get("continueUrl"),
      };
    } catch {
      return { mode: null, oobCode: null, continueUrl: null };
    }
  }, []);

  const [newPass, setNewPass] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  // If the route is opened without the magic params, show a friendly message
  if (mode !== "resetPassword" || !oobCode) {
    return (
      <Outer>
        <Card>
          <h1 style={{ marginTop: 0 }}>Password Reset</h1>
          <p>Invalid or missing link parameters.</p>
          {/* Hide this block if you prefer a cleaner screen */}
          <pre className="debug">
            {`mode=${mode ?? "(none)"}
oobCode=${oobCode ? "[present]" : "(none)"}
continueUrl=${continueUrl ?? "(none)"}`}
          </pre>
        </Card>
      </Outer>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newPass || newPass.length < 6) {
      setMsg("Please enter a password of at least 6 characters.");
      return;
    }

    setBusy(true);
    setMsg("Verifying reset link…");

    try {
      // 1) Make sure the code is still valid (throws if expired/used)
      await verifyPasswordResetCode(auth, oobCode);

      // 2) Save the new password
      setMsg("Updating password…");
      await confirmPasswordReset(auth, oobCode, newPass);

      // 3) Success → short toast + redirect
      setMsg("✅ Password updated. Redirecting…");

      const target =
        (continueUrl && safeContinueUrl(continueUrl)) || REDIRECT_PATH;

      setTimeout(() => {
        window.location.replace(target);
      }, 1200);
    } catch (err) {
      console.error(err);
      if (err?.code === "auth/invalid-action-code") {
        setMsg(
          "❌ This reset link is invalid or has expired. Please request a new reset email."
        );
      } else {
        setMsg(`❌ ${err?.message || "Could not update password."}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Outer>
      <Card>
        <h1 style={{ marginTop: 0 }}>Set a new password</h1>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="New password"
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            required
            minLength={6}
            className="input"
          />
          <button type="submit" disabled={busy} className="btn">
            {busy ? "Working…" : "Update password"}
          </button>
        </form>

        <div className="msg">{msg || "Enter a new password and submit."}</div>
      </Card>
      <style>{styles}</style>
    </Outer>
  );
}

/* ---------- helpers & styling ---------- */

function safeContinueUrl(url) {
  // Allow only same-origin or absolute http(s) URLs you trust.
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin === window.location.origin) return u.toString();
    if (
      /^https:\/\/(dat-gpt\.web\.app|dat-gpt\.firebaseapp\.com|dialectix\.com\.au)$/i.test(
        u.host
      )
    ) {
      return u.toString();
    }
  } catch {
    /* ignore */
  }
  return null;
}

function Outer({ children }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#0b1020",
        color: "#fff",
        padding: 16,
      }}
    >
      {children}
    </div>
  );
}

function Card({ children }) {
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 480,
        background: "#111a33",
        padding: 24,
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,.35)",
      }}
    >
      {children}
    </div>
  );
}

const styles = `
.input{
  width:100%;
  padding:10px 12px;
  border-radius:8px;
  border:1px solid #2b3b66;
  background:#0a1228;
  color:#fff;
}
.btn{
  margin-top:12px;
  width:100%;
  padding:10px 12px;
  border-radius:8px;
  border:none;
  background:#4f46e5;
  color:#fff;
  font-weight:600;
  cursor:pointer;
}
.btn[disabled]{ opacity:.7; cursor:default; }
.msg{ margin-top:12px; font-size:14px; opacity:.95; }
pre.debug{
  margin-top:10px;
  background:#0a1228;
  padding:12px;
  border-radius:8px;
  white-space:pre-wrap;
  word-break:break-word;
}
`;
